import {
  DEMO_MILESTONE_MINUTES,
  FunnelStage,
  JOURNEY_STORAGE_KEY,
  MILLIS_PER_SECOND,
  SECONDS_PER_MINUTE,
  STAGE_ORDER,
} from './constants'

// State is held in localStorage rather than in a module variable so the clock
// survives the full-page reloads the onboarding flow performs (Meta redirects,
// store switches). Every read goes back to storage for that reason — never
// cache it here.
interface JourneyState {
  stage: FunnelStage | null
  journeyStartedAt: number | null
  stageEnteredAt: number | null
  prevStage: FunnelStage | null
  prevStageSeconds: number
  demoActiveSeconds: number
  demoSessionNumber: number
  milestonesFired: number[]
}

export interface JourneyCounters {
  journey_stage: FunnelStage | null
  journey_seconds: number
  stage_seconds: number
  prev_stage: FunnelStage | null
  prev_stage_seconds: number
  demo_active_seconds: number
  demo_session_number: number
}

const EMPTY_STATE: JourneyState = {
  stage: null,
  journeyStartedAt: null,
  stageEnteredAt: null,
  prevStage: null,
  prevStageSeconds: 0,
  demoActiveSeconds: 0,
  demoSessionNumber: 0,
  milestonesFired: [],
}

// Fallback store for private-mode browsers, where localStorage access throws.
// Tracking degrades to per-tab instead of failing.
let memoryState: JourneyState | null = null

const readState = (): JourneyState => {
  try {
    const raw = window?.localStorage?.getItem(JOURNEY_STORAGE_KEY)
    if (!raw) {
      return memoryState ?? { ...EMPTY_STATE }
    }
    return { ...EMPTY_STATE, ...JSON.parse(raw) }
  } catch {
    return memoryState ?? { ...EMPTY_STATE }
  }
}

const writeState = (state: JourneyState): void => {
  memoryState = state
  try {
    window?.localStorage?.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Private mode / quota. memoryState already holds the update.
  }
}

const secondsSince = (timestamp: number | null): number => {
  if (!timestamp) {
    return 0
  }
  return Math.max(0, Math.round((Date.now() - timestamp) / MILLIS_PER_SECOND))
}

const isKnownStage = (stage: string): stage is FunnelStage =>
  STAGE_ORDER.includes(stage as FunnelStage)

const FIRST_STAGE = STAGE_ORDER[0]
const TERMINAL_STAGE = STAGE_ORDER[STAGE_ORDER.length - 1]

const buildCounters = (state: JourneyState): JourneyCounters => ({
  journey_stage: state.stage,
  journey_seconds: secondsSince(state.journeyStartedAt),
  stage_seconds: secondsSince(state.stageEnteredAt),
  prev_stage: state.prevStage,
  prev_stage_seconds: state.prevStageSeconds,
  demo_active_seconds: state.demoActiveSeconds,
  demo_session_number: state.demoSessionNumber,
})

// The counter bag merged onto every tracked event, so any existing funnel event
// answers "how long did this take, and how much demo time came first".
export const getJourneyCounters = (): JourneyCounters =>
  buildCounters(readState())

// Advances the funnel clock. Repeat and backwards stages are no-ops, so a
// screen that re-fires its event never restarts a stage timer.
export const markStage = (stage: string): JourneyCounters => {
  let state = readState()

  if (!isKnownStage(stage)) {
    return buildCounters(state)
  }

  // A signup arriving on a completed journey is a second store, not a step
  // backwards. Start clean so the finished journey's time never leaks into it.
  if (stage === FIRST_STAGE && state.stage === TERMINAL_STAGE) {
    resetJourney()
    state = readState()
  }

  const currentRank = state.stage ? STAGE_ORDER.indexOf(state.stage) : -1
  if (STAGE_ORDER.indexOf(stage) <= currentRank) {
    return buildCounters(state)
  }

  const now = Date.now()
  const advanced: JourneyState = {
    ...state,
    stage,
    journeyStartedAt: state.journeyStartedAt ?? now,
    stageEnteredAt: now,
    prevStage: state.stage,
    prevStageSeconds: secondsSince(state.stageEnteredAt),
  }
  writeState(advanced)
  return buildCounters(advanced)
}

// Adds measured active time and reports any milestone minutes crossed by this
// increment. Each milestone is reported once per merchant, never per session.
export const addDemoActiveSeconds = (seconds: number): number[] => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return []
  }

  const state = readState()
  const total = state.demoActiveSeconds + seconds
  const crossed = DEMO_MILESTONE_MINUTES.filter(
    (minutes) =>
      total >= minutes * SECONDS_PER_MINUTE &&
      !state.milestonesFired.includes(minutes)
  )

  writeState({
    ...state,
    demoActiveSeconds: total,
    milestonesFired: [...state.milestonesFired, ...crossed],
  })

  return crossed
}

// Called once per demo-store visit. Active seconds keep accumulating across
// sessions; only the session counter increments.
export const startDemoSession = (): number => {
  const state = readState()
  const demoSessionNumber = state.demoSessionNumber + 1
  writeState({ ...state, demoSessionNumber })
  return demoSessionNumber
}

// Clears the journey on logout and once activation completes, so a merchant
// signing up again starts from a clean clock.
export const resetJourney = (): void => {
  memoryState = null
  try {
    window?.localStorage?.removeItem(JOURNEY_STORAGE_KEY)
  } catch {
    // Private mode. memoryState is already cleared.
  }
}
